import { useState } from 'react';
import { useDAWStore } from '../store/daw-store';
import type { WorkspaceLayout } from '../store/daw-store';
import { X, Check, Trash2, Layout as LayoutIcon, Save } from 'lucide-react';
import { SaveLayoutModal } from './SaveLayoutModal';

interface WorkspaceLayoutSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WorkspaceLayoutSelector({ isOpen, onClose }: WorkspaceLayoutSelectorProps) {
  const { layouts, currentLayout, setLayout, deleteLayout, saveCurrentLayout, reduceMotion } = useDAWStore();
  const [showSaveModal, setShowSaveModal] = useState(false);

  if (!isOpen) return null;

  const handleLayoutClick = (layout: WorkspaceLayout) => {
    setLayout(layout);
    // Animate panel transitions (300ms) - or instant if reduce motion is enabled (Requirements 14.4, 14.5)
    const animationDuration = reduceMotion ? 0 : 300;
    setTimeout(() => {
      onClose();
    }, animationDuration);
  };

  const handleDeleteLayout = (layoutId: string, layoutName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Delete layout "${layoutName}"?`)) {
      deleteLayout(layoutId);
      // If deleted layout was active, revert to default
      if (currentLayout.id === layoutId) {
        const defaultLayout = layouts.find(l => l.id === 'default');
        if (defaultLayout) {
          setLayout(defaultLayout);
        }
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[var(--daw-bg-elevated)] border border-[var(--daw-border)] rounded-lg shadow-2xl w-[900px] max-h-[700px] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--daw-border)]">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <LayoutIcon className="w-5 h-5 text-[var(--daw-accent-primary)]" />
              <h2 className="text-lg font-semibold text-white">Select Workspace Layout</h2>
            </div>
            <button
              onClick={() => setShowSaveModal(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-[var(--daw-accent-primary)] hover:bg-[var(--daw-accent-hover)] rounded transition-colors flex items-center gap-2"
              title="Save current layout"
            >
              <Save className="w-4 h-4" />
              Save Current Layout
            </button>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[var(--daw-bg-tertiary)] rounded transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Layout Grid */}
        <div className="p-6 overflow-y-auto max-h-[600px]">
          <div className="grid grid-cols-3 gap-4">
            {layouts.map((layout: WorkspaceLayout) => (
              <LayoutPreviewCard
                key={layout.id}
                layout={layout}
                isActive={currentLayout.id === layout.id}
                isCustom={!['default', 'producer', 'performer'].includes(layout.id)}
                onClick={() => handleLayoutClick(layout)}
                onDelete={(e) => handleDeleteLayout(layout.id, layout.name, e)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Save Layout Modal */}
      <SaveLayoutModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={(name) => {
          saveCurrentLayout(name);
          setShowSaveModal(false);
        }}
      />
    </div>
  );
}

interface LayoutPreviewCardProps {
  layout: WorkspaceLayout;
  isActive: boolean;
  isCustom: boolean;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

function LayoutPreviewCard({ layout, isActive, isCustom, onClick, onDelete }: LayoutPreviewCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative p-4 rounded-lg border-2 transition-all duration-200
        ${isActive 
          ? 'border-[var(--daw-accent-primary)] bg-[var(--daw-bg-tertiary)]' 
          : 'border-[var(--daw-border)] bg-[var(--daw-bg-secondary)] hover:border-[var(--daw-border-light)] hover:bg-[var(--daw-bg-tertiary)]'
        }
      `}
    >
      {/* Active Indicator */}
      {isActive && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-[var(--daw-accent-primary)] rounded-full flex items-center justify-center z-10">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}

      {/* Delete Button for Custom Layouts */}
      {isCustom && isHovered && (
        <button
          onClick={onDelete}
          className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center z-10 transition-colors"
          title="Delete layout"
        >
          <Trash2 className="w-3.5 h-3.5 text-white" />
        </button>
      )}

      {/* Layout Name */}
      <div className="text-left mb-3">
        <h3 className="text-sm font-semibold text-white">{layout.name}</h3>
      </div>

      {/* Layout Thumbnail Preview */}
      <div className="bg-[var(--daw-bg-primary)] rounded border border-[var(--daw-border-light)] p-2 mb-3">
        <LayoutThumbnail layout={layout} />
      </div>

      {/* Layout Details */}
      <div className="text-xs text-gray-400 space-y-1">
        <div className="flex justify-between">
          <span>Browser:</span>
          <span className={layout.browserVisible ? 'text-green-400' : 'text-gray-500'}>
            {layout.browserVisible ? 'Visible' : 'Hidden'}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Mixer:</span>
          <span className="text-gray-300">{layout.mixerHeight}px</span>
        </div>
        <div className="flex justify-between">
          <span>Channel Rack:</span>
          <span className="text-gray-300">{layout.channelRackHeight}px</span>
        </div>
      </div>
    </button>
  );
}

interface LayoutThumbnailProps {
  layout: WorkspaceLayout;
}

function LayoutThumbnail({ layout }: LayoutThumbnailProps) {
  // Calculate relative widths for visual representation
  const mixerRatio = layout.mixerHeight / (layout.mixerHeight + layout.channelRackHeight);
  const channelRackRatio = layout.channelRackHeight / (layout.mixerHeight + layout.channelRackHeight);

  return (
    <div className="flex gap-1 h-20">
      {/* Browser Panel */}
      {layout.browserVisible && (
        <div 
          className="w-1/4 bg-[var(--daw-accent-primary)]/20 border border-[var(--daw-accent-primary)]/40 rounded flex items-center justify-center"
          title="Browser Panel"
        >
          <div className="text-[8px] text-[var(--daw-accent-primary)] font-medium rotate-90">
            BROWSER
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col gap-1 ${layout.browserVisible ? '' : 'w-full'}`}>
        {/* Playlist/Timeline Area */}
        <div 
          className="flex-1 bg-[var(--daw-bg-secondary)] border border-[var(--daw-border)] rounded flex items-center justify-center"
          title="Playlist/Timeline"
        >
          <div className="text-[8px] text-gray-500 font-medium">
            PLAYLIST
          </div>
        </div>

        {/* Bottom Panels Container */}
        <div className="flex gap-1" style={{ height: '40%' }}>
          {/* Mixer Panel */}
          <div 
            className="bg-blue-500/20 border border-blue-500/40 rounded flex items-center justify-center"
            style={{ 
              width: `${mixerRatio * 100}%`,
              minWidth: '30%'
            }}
            title={`Mixer (${layout.mixerHeight}px)`}
          >
            <div className="text-[8px] text-blue-400 font-medium">
              MIXER
            </div>
          </div>

          {/* Channel Rack Panel */}
          <div 
            className="bg-purple-500/20 border border-purple-500/40 rounded flex items-center justify-center"
            style={{ 
              width: `${channelRackRatio * 100}%`,
              minWidth: '30%'
            }}
            title={`Channel Rack (${layout.channelRackHeight}px)`}
          >
            <div className="text-[8px] text-purple-400 font-medium">
              RACK
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
